"use client";

import { useId, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { sanitize } from "@/lib/sanitize";

interface ExpandableContentProps {
    content: string;
    /** Accessible name of the reading panel (e.g. "AltusHost savings guide"). */
    label?: string;
    /**
     * Store pages: the "Read More" / "Show Less" label is drawn by CSS (`.cs-btn--label`) and named through aria-label, so the control puts
     * no text between the paragraphs of the store's text in the DOM. Looks and works the same. Default false (home page unchanged).
     */
    cleanToggle?: boolean;
}

/**
 * Editorial text with Read More / Show Less. The whole text is always in the DOM (collapsed only by CSS), so
 * search engines and assistive technology read all of it. Typography comes from `.cs-prose` (design-shell.css);
 * the reading panel (`.cs-reading`) is rendered here, so home and store pages share one component.
 */
export default function ExpandableContent({ content, label, cleanToggle = false }: ExpandableContentProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const copyId = useId();
    const sanitizedContent = sanitize(content);

    return (
        <section className="cs-reading cs-readmore" data-collapsed={!isExpanded} aria-label={label}>
            <div id={copyId} className="cs-readmore__copy prose prose-invert cs-prose cs-prose--lead max-w-none">
                <div
                    className="cs-measure"
                    dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                    suppressHydrationWarning={true}
                />
            </div>

            <div className="cs-readmore__toggle">
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    aria-expanded={isExpanded}
                    aria-controls={copyId}
                    {...(cleanToggle ? { "aria-label": isExpanded ? "Show Less" : "Read More" } : {})}
                    className={cleanToggle ? "cs-btn cs-btn--label" : "cs-btn"}
                >
                    {cleanToggle ? (
                        isExpanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />
                    ) : isExpanded ? (
                        <>
                            Show Less <ChevronUp aria-hidden="true" />
                        </>
                    ) : (
                        <>
                            Read More <ChevronDown aria-hidden="true" />
                        </>
                    )}
                </button>
            </div>
        </section>
    );
}
